import { useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { CoworkUserError } from '../../../shared/coworkUserError';
import { i18nService } from '../../services/i18n';
import { coworkRecoveryActions } from '../../utils/coworkRecovery';
import CoworkErrorReportControl from './CoworkErrorReportControl';

/** 独立错误卡保留BoMate主题色/图标/反馈布局；恢复动作不改写历史消息或自动重新计费。 */
export default function CoworkUserErrorCard({ error, sessionId, actionable, onContinue }: {
  error: CoworkUserError;
  /** 恢复入口只绑定当前会话，切模型事件不得影响其他会话。 */
  sessionId: string;
  /** 仅最新轮且会话空闲时可恢复；历史卡片仍允许复制编号和反馈。 */
  actionable: boolean;
  onContinue?: (prompt: string) => void | boolean | Promise<void | boolean>;
}) {
  const actions = coworkRecoveryActions(error);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [now, setNow] = useState(Date.now);
  const locked = useRef(false), alive = useRef(false);
  const waiting = actions.retryAt > now;
  const disabled = !actionable || busy || submitted;
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!waiting) return;
    // 等待结束只启用按钮，不调用继续接口；较长等待分段设置以避免定时器溢出。
    const timer = window.setTimeout(() => setNow(Date.now()), Math.min(actions.retryAt - now + 1, 60_000));
    return () => window.clearTimeout(timer);
  }, [actions.retryAt, now, waiting]);

  /** 同步锁覆盖双击和异步提交；只在明确失败时允许再次手工尝试。 */
  const resume = async (): Promise<void> => {
    if (locked.current || disabled || waiting || !onContinue) return;
    locked.current = true; setBusy(true); setFailed(false);
    try {
      const confirmed = await onContinue(i18nService.t('coworkContinueInstruction'));
      if (!alive.current) return;
      if (confirmed === false) { locked.current = false; setFailed(true); }
      else setSubmitted(true);
    } catch {
      locked.current = false;
      if (alive.current) setFailed(true);
    } finally { if (alive.current) setBusy(false); }
  };

  return <div className="w-full rounded-xl border border-l-[3px] border-claude-border/70 bg-claude-bg/60 px-4 py-3 text-claude-textSecondary dark:border-claude-darkBorder/70 dark:bg-claude-darkBg/40 dark:text-claude-darkTextSecondary"
    style={{ borderLeftColor: 'var(--accent-color, #3B82F6)' }}>
    <div data-cowork-user-error-layout className="text-xs leading-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-none text-claude-accent" aria-hidden="true" />
          <div className="min-w-0 flex-1"><p className="break-words font-semibold text-claude-text dark:text-claude-darkText">{error.title}</p>
            <p className="mt-1 break-words">{error.message}</p>
            {error.guidance.length > 0 && <ul className="mt-1 space-y-1">{error.guidance.map((value, index) => <li key={index} className="break-words">{value}</li>)}</ul>}
          </div>
        </div>
        {actions.resume && onContinue && <button type="button" onClick={() => void resume()} disabled={disabled || waiting}
          className="inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-claude-accent transition-colors hover:bg-claude-accent/10 disabled:opacity-50">
          <ArrowPathIcon className={`h-3.5 w-3.5 ${busy ? 'motion-safe:animate-spin' : ''}`} aria-hidden="true" />
          {i18nService.t(submitted ? 'coworkRecoverySubmitted' : busy ? 'coworkRecoverySending' : waiting ? 'coworkRecoveryWait' : 'coworkRecoveryContinue')}
        </button>}
      </div>
      {failed && <p role="status" className="mt-2 text-amber-700 dark:text-amber-300">{i18nService.t('coworkRecoveryFailed')}</p>}
      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <CoworkErrorReportControl userError={error} />
        <div className="flex flex-wrap gap-2">
          {actions.account && <button type="button" disabled={disabled} onClick={() => window.dispatchEvent(new Event('bomate:open-account'))}
            className="rounded-md px-2 py-1 text-claude-accent transition-colors hover:bg-claude-accent/10 disabled:opacity-50">{i18nService.t(error.category === 'quota' ? 'creditTitle' : 'authAccount')}</button>}
          {actions.chooseModel && <button type="button" disabled={disabled} onClick={() => window.dispatchEvent(new CustomEvent('bomate:choose-model', { detail: { sessionId } }))}
            className="rounded-md px-2 py-1 text-claude-accent transition-colors hover:bg-claude-accent/10 disabled:opacity-50">{i18nService.t('selectModel')}</button>}
        </div>
      </div>
    </div>
  </div>;
}
